<template>
  <v-app>
    <v-main class="main">

      <!-- Sidebar -->
      <Sidebar :drawerOpen="drawerOpen" :collapsed="isSidebarCollapsed" :isMobile="isMobile"
        @update:drawerOpen="drawerOpen = $event" />

      <v-container fluid class="main-content pa-4">

        <!-- Header -->
        <Header :isDarkMode="isDarkMode" :isMobile="isMobile" @toggle-sidebar="toggleSidebar" />

        <div class="content">

          <!-- FULL SCREEN LOADING -->
          <div v-if="loading" class="pa-6">

            <!-- Title skeleton -->
            <v-skeleton-loader
              type="heading"
              width="300"
              class="mb-6"
            />

            <!-- Grid skeleton -->
            <v-row>
              <v-col cols="12" md="4" v-for="n in 6" :key="n">
                <v-card class="pa-6" rounded="lg">

                  <v-skeleton-loader type="image" height="40" class="mb-4" />

                  <v-skeleton-loader type="heading" width="70%" />
                  <v-skeleton-loader type="text" width="50%" class="mt-2" />

                  <v-divider class="my-4" />

                  <v-skeleton-loader type="text" width="60%" />
                  <v-skeleton-loader type="text" width="40%" class="mt-2" />

                  <v-divider class="my-4" />

                  <v-skeleton-loader type="text" width="80%" />

                </v-card>
              </v-col>
            </v-row>

          </div>


          <v-row class="g-4 mt-4 mb-8" dense>

            <v-col cols="12" sm="12">
              <div class="column-title mb-6">
                <h5 class="m-0 text-left">Investment Plans</h5>
                <hr class="title-separator" />
              </div>
            </v-col>

            <!-- Product Cards -->
            <v-col v-for="(product, index) in products" :key="index" cols="12" md="4" class="pa-2">
              <v-card class="product-card pa-6 h-100" flat>

                <!-- TOP: Logo + Name -->
                <div class="d-flex align-center gap-2 mb-4">
                  <img :src="product.product_image ? `${STORAGE_URL}/${product.product_image}` : defaultImage"
                    class="product-image" />
                  <div class="text-left">
                    <div class="font-weight-bold text-body-1">
                      {{ product.product_name }}
                    </div>
                    <div class="text-caption text-grey">
                      {{ product.partner.name }}
                    </div>
                  </div>
                </div>

                <!-- INNER CARD -->
                <v-card class="inner-mini-card pa-2 mb-3" flat>
                  <div class="d-flex justify-space-between">

                    <!-- LEFT: Maturity -->
                    <div class="text-left">
                      <div class="text-caption text-grey">Maturity Date</div>
                      <div class="font-weight-medium">
                        {{ formatDate(product.maturity_date) }}
                      </div>
                    </div>

                    <!-- RIGHT: Rate -->
                    <div class="text-right">
                      <div class="text-caption text-grey">Rate</div>
                      <div class="font-weight-medium text-green">
                        {{ formatNumber(product.interest) }}%
                      </div>
                    </div>

                  </div>
                </v-card>

                <!-- BOTTOM ROW -->
                <div class="d-flex justify-space-between align-center mt-auto">

                  <!-- Risk -->
                  <div class="text-caption">
                    <span :class="getRiskClass(product.risk_category.name)">
                      {{ product.risk_category.name }}
                    </span>
                  </div>

                  <!-- View Details -->
                  <div class="d-flex align-center gap-2" >
                    <span class="text-caption font-weight-medium clickable-title" @click="viewProductDetails(product)">
                      View Details
                    </span>
                    <v-icon size="16" class="ms-1">mdi-arrow-right-circle</v-icon>
                  </div>

                </div>

              </v-card>
            </v-col>

          </v-row>
        </div>

        
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue"
import { useRoute, useRouter } from "vue-router"
import Sidebar from "@/components/Sidebar.vue"
import Header from "@/components/Header.vue"
import { getRiskClass, formatDate, formatNumber } from "@/util"
import { useInvStore } from "@/store/investment"
import { STORAGE_URL } from "@/api/env"
import avatarImage from '@/assets/crowdital-icon.png'


//const auth = useAuthStore()
const inv = useInvStore()
const route = useRoute();
const router = useRouter()
const productTypeId = route.params.id

const isSidebarCollapsed = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)
const defaultImage = avatarImage
const loading = ref(true)

/* Mobile */
const toggleSidebar = () => {
  if (isMobile.value) drawerOpen.value = !drawerOpen.value
  else isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
  drawerOpen.value = !isMobile.value
}

/* Lifecycle */
onMounted(async () => {
  checkMobile()
  window.addEventListener("resize", checkMobile)
  loading.value = true 
  await inv.loadProducts(productTypeId)
  loading.value = false
})


onUnmounted(() => {
  window.removeEventListener("resize", checkMobile)
})

/* Products */
const products = computed(() => inv.products || [])

const viewProductDetails = (product) => {
  router.push(`/product-detail/${product.slug}`)
}

</script>

<style scoped>
.main {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  background: #f8fafc;
}

.content {
  padding-left: 10%;
  padding-right: 10%;
}

/* Card */
.table-card {
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
}

/* Header */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

/* Table */
.modern-table {
  border: none;
}

/* Product */
.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-card {
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
}

.product-image {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  margin-right: 2%;
}

.product-name {
  font-weight: 700;
}

.product-desc {
  font-size: 16px;
  color: #64748b;
}

.inner-mini-card {
  border: 1px solid #eae5e5;
  border-radius: 8px;
  background: #fff;
}

/* Column title */
.column-title h5 {
  font-size: 1.1rem;
  font-weight: 400;
  margin-bottom: 2.5%;
}

.clickable-title {
  cursor: pointer;
  transition: 0.2s ease;
}

.clickable-title:hover {
  opacity: 0.7;
}

/* Status */
.status-pill {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-active {
  background: #ecfdf5;
  color: #16a34a;
}

.status-inactive {
  background: #fef2f2;
  color: #dc2626;
}

/* Search input */
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 8px;
  color: #64748b;
}

.search-wrapper input {
  padding-left: 28px;
}

/* Button */
.theme-button {
  height: 46px;
  background: #000;
  color: #fff;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.25s ease;
}

:deep(.modern-table .p-datatable-thead > tr > th) {
  background: #eef7f0;
  color: #065f46;
  font-weight: 600;
  border-bottom: 2px solid #c1edc3;
}

.p-datatable-tbody > tr {
  cursor: pointer;
}

.p-datatable-tbody > tr:hover {
  background-color: #f5f5f5;
}
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Mobile */
@media (max-width: 600px) {
  .content {
    padding: 0;
  }

  .avatar-mobile {
    width: 32px;
    height: 32px;
  }

  .title-mobile {
    font-size: 0.9rem;
  }

  .icon-mobile {
    font-size: 16px;
  }
}
</style>
